import React, { useEffect, useState } from "react";
import { XIcon, AppleLogoIcon, SparkleIcon } from "@phosphor-icons/react";

/**
 * Bidorah "Coming Soon" announcement banner.
 *
 * Surfaces two roadmap items prominently:
 *   1. Live sale streaming is rolling out in waves to approved Founder
 *      Sellers — sets expectations so buyers don't bounce when they don't
 *      see a wall of live shows yet.
 *   2. iPhone app is coming soon — Android is live on Google Play; iOS
 *      build + App Store submission is in motion.
 *
 * The "Coming soon" pill itself sparkles with an animated shimmer + a pair of
 * gentle sparkle stars, drawing the eye without being noisy.  The pill sits
 * on top, centered, with the Live-sales and iPhone-app messages stacked
 * directly beneath it — all perfectly aligned along the horizontal center.
 * Dismissible; stays dismissed across page navigations + reloads via
 * localStorage (keyed by `storageKey`).
 *
 * Usage:
 *   <ComingSoonBanner />
 *   <ComingSoonBanner storageKey="coming-soon-banner-v3" />
 */
export default function ComingSoonBanner({
  storageKey = "bidorah:coming-soon-banner:2026-02-live-ios",
}) {
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid SSR flash

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  const close = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // localStorage disabled — fine, we just won't persist.
    }
  };

  if (dismissed) return null;

  return (
    <div
      className="relative bg-[#1D78AB] text-white"
      data-testid="coming-soon-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 pr-12 pl-12 flex flex-col items-center justify-center gap-1.5 text-center text-xs sm:text-sm">
        {/* Sparkling "Coming soon" pill — sits on top, centered. */}
        <span
          className="bidora-coming-soon-pill relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[10px] sm:text-[11px] whitespace-nowrap"
          data-testid="coming-soon-banner-pill"
        >
          <SparkleIcon size={12} weight="fill" className="bidora-coming-soon-sparkle bidora-coming-soon-sparkle--a" aria-hidden="true" />
          Coming soon
          <SparkleIcon size={10} weight="fill" className="bidora-coming-soon-sparkle bidora-coming-soon-sparkle--b" aria-hidden="true" />
        </span>

        {/* Roadmap message. The Apple logo is rendered as a true inline
            element directly before "iPhone" so when the line wraps on a
            narrow viewport the icon stays glued to the word "iPhone" on
            the same line — never floating away to a separate row. */}
        <div className="font-semibold leading-snug text-center">
          <AppleLogoIcon
            size={13}
            weight="fill"
            className="inline-block align-text-bottom mr-1 -mt-px"
            aria-hidden="true"
          />
          <span>iPhone app</span>{" "}
          <span className="text-white/80 font-normal">
            — launching on the App Store soon. Android is live now.
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss announcement"
        className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-3 h-7 w-7 grid place-items-center rounded-full hover:bg-white/15 transition-colors"
        data-testid="coming-soon-banner-dismiss"
      >
        <XIcon size={15} weight="bold" />
      </button>
    </div>
  );
}
